import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider } from '@/contexts/AuthContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import DashboardPage from '@/pages/DashboardPage';
import LoginPage from '@/pages/LoginPage';
import StudentsPage from '@/pages/StudentsPage';
import ClassesPage from '@/pages/ClassesPage';
import TestsPage from '@/pages/TestsPage';
import CreateTestPage from '@/pages/CreateTestPage';
import QuestionPreviewPage from '@/pages/QuestionPreviewPage';
import ArchivePage from '@/pages/ArchivePage';
import SettingsPage from '@/pages/SettingsPage';
import StudentDetailPage from '@/pages/StudentDetailPage';
import AddStudentPage from '@/pages/AddStudentPage';
import ImportStudentsPage from '@/pages/ImportStudentsPage';
import EditStudentPage from '@/pages/EditStudentPage';
import ClassDetailPage from '@/pages/ClassDetailPage';
import AddClassPage from '@/pages/AddClassPage';
import EditClassPage from '@/pages/EditClassPage';
import ManageClassStudentsPage from '@/pages/ManageClassStudentsPage';
import TestDetailPage from '@/pages/TestDetailPage';
import TestResultsPage from '@/pages/TestResultsPage';
import TestSchedulePage from '@/pages/TestSchedulePage';
import TestScheduleEditPage from '@/pages/TestScheduleEditPage';
import TestAssignPage from '@/pages/TestAssignPage';
import TestAssignEditPage from '@/pages/TestAssignEditPage';
import QuestionsPage from '@/pages/QuestionsPage';
import BatchesPage from '@/pages/BatchesPage';
import AddBatchPage from '@/pages/AddBatchPage';
import EditBatchPage from '@/pages/EditBatchPage';
import TestSettingsPage from '@/pages/TestSettingsPage';
import FirstTimePasswordResetPage from '@/pages/FirstTimePasswordResetPage';
import PasswordSetupPage from '@/pages/PasswordSetupPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/first-time-password-reset" element={<FirstTimePasswordResetPage />} />
          <Route path="/setup-password" element={<PasswordSetupPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
          {/* Dashboard */}
          <Route index element={<DashboardPage />} />

          {/* Students */}
          <Route path="students" element={<StudentsPage />} />
          <Route path="students/new" element={<AddStudentPage />} />
          <Route path="students/import" element={<ImportStudentsPage />} />
          <Route path="students/:studentId" element={<StudentDetailPage />} />
          <Route path="students/:studentId/edit" element={<EditStudentPage />} />

          {/* Classes */}
          <Route path="classes" element={<ClassesPage />} />
          <Route path="classes/new" element={<AddClassPage />} />
          <Route path="classes/:classId" element={<ClassDetailPage />} />
          <Route path="classes/:classId/edit" element={<EditClassPage />} />
          <Route path="classes/:classId/students" element={<ManageClassStudentsPage />} />

          {/* Tests */}
          <Route path="tests" element={<TestsPage />} />
          <Route path="tests/new" element={<CreateTestPage />} />
          <Route path="tests/create" element={<CreateTestPage />} />
          <Route path="tests/preview/:testId" element={<QuestionPreviewPage />} />
          <Route path="tests/schedule/:testId" element={<TestSchedulePage />} />
          <Route path="tests/:testId/schedule" element={<TestScheduleEditPage />} />
          <Route path="tests/assign/:testId" element={<TestAssignPage />} />
          <Route path="tests/:testId/assign" element={<TestAssignEditPage />} />
          <Route path="tests/:testId" element={<TestDetailPage />} />
          <Route path="tests/:testId/edit" element={<TestDetailPage />} />
          <Route path="tests/results" element={<TestResultsPage />} />

          {/* Questions */}
          <Route path="questions" element={<QuestionsPage />} />

          {/* Archive */}
          <Route path="archive" element={<ArchivePage />} />

          {/* Settings */}
          <Route path="settings" element={<SettingsPage />}>
            <Route path="batches" element={<BatchesPage />} />
            <Route path="test-settings" element={<TestSettingsPage />} />
          </Route>

          {/* Batch Management Routes (outside settings) */}
          <Route path="batches/new" element={<AddBatchPage />} />
          <Route path="batches/:id/edit" element={<EditBatchPage />} />
        </Route>

          {/* Fallback route - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
